import { useMutation, useQueryClient } from '@tanstack/react-query';
import { surveysQueryKeys } from '../Constants/queryKeys';
import { restoreSurvey } from '../Services/Surveys.service';
import type { Survey, SurveyListResponse } from '../Types/Surveys.types';
import { surveyTitle } from '../Utils/surveyFormat';
import { useActionToast } from '@/Hooks/useActionToast';

/**
 * Undoes the soft delete of a survey — `PATCH /:id/restore`, behind `survey:update`.
 *
 * The `200` carries the whole survey, so the row is **put back from the response** rather than
 * refetched. Listings that do not already hold the id are left alone: one without `includeDeleted`
 * never showed the row, and writing it there would put a survey in a list that did not ask for it.
 *
 * ## It comes back off the air, and the caller has to say so
 *
 * The BFF forces `isActive: false` and keeps `approvalStatus`. That is deliberate: delivery needs
 * active **and** approved **and** not deleted, so restoring alone can never drop a modal in front
 * of real people at the instant somebody undoes a delete.
 *
 * The trap it creates is a person who restores, reads "Aprovada" on the card, and waits for a
 * delivery that will never happen. So this hook does not settle for a toast — the caller is handed
 * the restored survey and is expected to offer the way back on the air. Whoever changes this must
 * keep that promise somewhere.
 */
export const useRestoreSurvey = () => {
  const queryClient = useQueryClient();
  const toast = useActionToast();

  return useMutation({
    mutationFn: (id: string) => restoreSurvey(id),
    onSuccess: restored => {
      queryClient.setQueriesData<SurveyListResponse>(
        { queryKey: surveysQueryKeys.lists() },
        current =>
          current && current.data.some(survey => survey.id === restored.id)
            ? {
                ...current,
                data: current.data.map(survey => (survey.id === restored.id ? restored : survey)),
              }
            : current,
      );
      queryClient.setQueryData<Survey>(surveysQueryKeys.detail(restored.id), current =>
        current ? { ...current, ...restored } : undefined,
      );

      toast.success(
        `"${surveyTitle(restored)}" restaurada`,
        'Voltou desativada, de propósito: restaurar não coloca uma pesquisa no ar.',
      );
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível restaurar a pesquisa', error);
    },
  });
};
